import { toast } from "react-toastify";
import api from "./api";
import { scaleUnit, parseScaleUnit } from "../utils/constants";
import {
  ok, getMe, can, ORG_SCOPES, findRecord, myRecordSummary, recordDetail, listRecords, splitItems, totalWeight,
  employeeFromRecord, toEmployee, getLookupsCached, cachedItem, itemBody, cycleById, isStageOpen, RESULT_STATES,
  DIRECTION_TO_API, DIRECTION_TO_UI, FREQUENCY_TO_API, FREQUENCY_TO_UI, utcToday, isoUtc, nameOf, reasonOf, pMapSettled,
  recordSection, extensionsFromHistory, sheetStatusOf,
} from "./core";

const slug = (name) => String(name || "").toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "ITEM";

// [NEW] definition_scope / owner_employee_id (migration 013): ORG = organization-wide (HR/Admin),
// TEAM = created by a manager for their own team. The backend sets both automatically.
// [NEW] Optional applicability of a KRA/KPI: cost centre and legal entity (empty = applies to all).
const orgScope = (r) => ({ costCenter: r.cost_center || "", legalEntity: r.legal_entity || "" });

const ownership = (r) => {
  const scope = r.definition_scope || "ORG";
  const ownerId = r.owner_employee_id || null;
  return { scope, ownerId, ownerName: ownerId ? nameOf(ownerId) : "" };
};

export const toKra = (r) => ({
  id: String(r.kra_id),
  code: r.kra_code,
  name: r.kra_name,
  description: r.description || "",
  departmentId: r.department_id ? String(r.department_id) : null,
  designationId: r.designation_id ? String(r.designation_id) : null,
  measurementMethod: r.measurement_method || "",
  unit: r.measurement_unit || "%",
  frequency: FREQUENCY_TO_UI[r.measurement_frequency] || "Monthly",
  direction: DIRECTION_TO_UI[r.measurement_direction] || "higher",
  origin: r.origin || "LIBRARY",
  active: Boolean(r.is_active),
  ...orgScope(r),
  ...ownership(r),
});

export const toKpi = (r) => ({
  id: String(r.kpi_id),
  code: r.kpi_code,
  name: r.kpi_name,
  description: r.description || "",
  unit: r.measurement_unit || "%",
  frequency: FREQUENCY_TO_UI[r.measurement_frequency] || "Once",
  direction: DIRECTION_TO_UI[r.measurement_direction] || "higher",
  active: Boolean(r.is_active),
  parentKraId: r.parent_kra_id ? String(r.parent_kra_id) : null,
  origin: r.origin || "LIBRARY",
  ...orgScope(r),
  ...ownership(r),
});

// Who may maintain the KRA/KPI library, and how far.
//   ORG  : HR/Admin (master.configure, organization scope) - every definition.
//   TEAM : manager (kra_kpi.library.team.manage) - organization-wide definitions read-only, own team definitions editable.
export function libraryScopeOf(me) {
  if (can(me, "master.configure", ORG_SCOPES)) return "ORG";
  if (can(me, "kra_kpi.library.team.manage", ["TEAM", "SYSTEM_ALL"])) return "TEAM";
  return null;
}

export async function getLibraryAccess() {
  const me = await getMe();
  const scope = libraryScopeOf(me);
  return ok({
    scope,
    meId: me.employee_id,
    canRead: Boolean(scope),
    canCreate: Boolean(scope),
    canMapKpiParent: scope === "ORG",
    canImport: scope === "ORG",
    canManageCompetencies: scope === "ORG",
  });
}

export const canEditDefinition = (row, access) => Boolean(access?.scope) && (access.scope === "ORG" || (row.scope === "TEAM" && row.ownerId === access.meId));

function libraryBody(kind, form, existing) {
  const body = {
    code: form.code || existing?.code || `${kind.toUpperCase()}-${slug(form.name)}-${Date.now().toString(36).slice(-4).toUpperCase()}`,
    name: String(form.name || "").trim(),
    description: String(form.description || "").trim(),
    measurement_unit: form.unit || existing?.unit || "%",
    measurement_method: kind === "kra" ? (form.measurementMethod || "Direction-aware target/actual ratio") : "Direction-aware target/actual ratio",
    measurement_frequency: FREQUENCY_TO_API[form.frequency] || "ONCE",
    measurement_direction: DIRECTION_TO_API[form.direction] || "HIGHER_IS_BETTER",
    department_id: kind === "kra" && form.departmentId ? Number(form.departmentId) : null,
    designation_id: kind === "kra" && form.designationId ? Number(form.designationId) : null,
    is_active: form.active ?? existing?.active ?? true,
    reason: form.reason || "Library updated",
  };
  // [NEW] cost_center / legal_entity are sent only when chosen (or when an old value is being cleared).
  if (form.costCenter || existing?.costCenter) body.cost_center = form.costCenter || null;
  if (form.legalEntity || existing?.legalEntity) body.legal_entity = form.legalEntity || null;
  return body;
}

async function list(kind) {
  const me = await getMe();
  if (!libraryScopeOf(me)) return [];
  await getLookupsCached();
  const { data } = await api.get(`/library/${kind}`);
  return data.map(kind === "kra" ? toKra : toKpi)
    .map((row) => ({ ...row, ownedByMe: row.scope === "TEAM" && row.ownerId === me.employee_id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export const getKraList = async () => ok(await list("kra"));
export const getKpiList = async () => ok(await list("kpi"));

async function save(kind, id, form) {
  const existing = id ? (await list(kind)).find((x) => x.id === String(id)) : null;
  const body = libraryBody(kind, { ...form, active: existing ? existing.active : true }, existing);
  const send = (b) => (id ? api.put(`/library/${kind}/${id}`, b) : api.post(`/library/${kind}`, b));
  let data;
  try {
    ({ data } = await send(body));
  } catch (err) {
    // [FIX] A backend without the cost_center / legal_entity columns rejects them (422, extra fields not allowed).
    // Save the KRA/KPI without them instead of failing, and tell the user they were not stored.
    const detail = JSON.stringify(err?.response?.data || err?.data || err?.message || "");
    const orgFields = ["cost_center", "legal_entity"].filter((f) => f in body);
    if (orgFields.length && /cost_center|legal_entity/.test(detail)) {
      const plain = { ...body };
      orgFields.forEach((f) => delete plain[f]);
      ({ data } = await send(plain));
      toast.warning(`${kind.toUpperCase()} saved, but the cost centre / legal entity were not stored: the backend does not support them yet.`);
    } else {
      throw err;
    }
  }
  return ok(kind === "kra" ? toKra(data) : toKpi(data));
}

async function toggle(kind, id) {
  const existing = (await list(kind)).find((x) => x.id === String(id));
  if (!existing) throw new Error("Library item not found.");
  const body = libraryBody(kind, { ...existing, active: !existing.active, reason: existing.active ? "Deactivated" : "Activated" }, existing);
  const { data } = await api.put(`/library/${kind}/${id}`, body);
  return ok(kind === "kra" ? toKra(data) : toKpi(data));
}

export const createKra = (payload) => save("kra", null, payload);
export const updateKra = (kraId, payload) => save("kra", kraId, payload);
export const toggleKra = (kraId) => toggle("kra", kraId);
export const createKpi = (payload) => save("kpi", null, payload);
export const updateKpi = (kpiId, payload) => save("kpi", kpiId, payload);
export const toggleKpi = (kpiId) => toggle("kpi", kpiId);

// [NEW] DELETE /library/{kind}/{master_id} - permanently deletes a KRA/KPI master that no template or
// assignment uses. Needs master.configure (organization scope) and a reason; answers 409 RECORD_IN_USE otherwise.
const removeMaster = async (kind, id, reason) => {
  const text = String(reason ?? "").trim();
  if (!text) throw new Error("Please give a reason for deleting this item.");
  const { data } = await api.delete(`/library/${kind}/${id}`, { data: { reason: reasonOf(text) } });
  return ok(data);
};
export const deleteKra = (kraId, reason) => removeMaster("kra", kraId, reason);
export const deleteKpi = (kpiId, reason) => removeMaster("kpi", kpiId, reason);
export async function canDeleteLibraryMaster() {
  return can(await getMe(), "master.configure", ORG_SCOPES);
}

function lockReason(record, cycle, extensions) {
  if (record.current_result_revision_id || RESULT_STATES.includes(record.record_status)) return "Appraisal finalized. Assignments for this cycle are locked.";
  if (record.current_stage !== "GOAL_SETTING") return "The goal plan has been submitted or approved, so KRA and KPI assignments are locked.";
  if (!["DRAFT", "CHANGES_REQUESTED"].includes(record.goal_plan?.plan_status)) return "The goal plan is not editable right now.";
  if (!isStageOpen(cycle, "goalSetting", extensions)) return "The goal-setting window of this cycle is not open (and no deadline extension is active for this person).";
  return null;
}

async function assignmentView(summary) {
  const [record, lookups] = await Promise.all([recordDetail(summary.performance_record_id), getLookupsCached()]);
  const cycle = await cycleById(record.cycle_id);
  const { kras, kpis } = splitItems(record);
  const extensions = extensionsFromHistory(await recordSection(record.performance_record_id, "history", []));
  const reason = lockReason(record, cycle, extensions);
  return {
    recordId: record.performance_record_id,
    employee: employeeFromRecord(record, lookups),
    kras,
    kpis,
    kraWeightage: totalWeight(kras),
    kpiWeightage: totalWeight(kpis),
    kpiWeightageByKraItem: Object.fromEntries(
      kras.map((kra) => [kra.id, totalWeight(kpis.filter((kpi) => kpi.parentKraItemId === kra.id))])
    ),
    locked: Boolean(reason),
    lockReason: reason,
    stage: record.current_stage,
    trackingOpen: record.current_stage === "TRACKING",
  };
}

export async function getEmployeeKraKpi(cycleId, employeeId) {
  return ok(await assignmentView(await findRecord(cycleId, employeeId)));
}

export async function getMyKraKpi(cycleId) {
  return ok(await assignmentView(await myRecordSummary(cycleId)));
}

// [UPDATED] Nobody types a KPI weight. The backend still needs the KPIs under a KRA to total 100%, so the
// frontend shares 100% equally between them (2 KPIs = 50 + 50, 3 KPIs = 33.33 + 33.33 + 33.34) and keeps it
// balanced when a KPI is added or removed. Equal shares = the KRA scores the average of its KPIs.
export function equalShares(count) {
  if (!count) return [];
  const base = Math.floor(10000 / count) / 100;
  const shares = Array(count).fill(base);
  shares[count - 1] = Math.round((100 - base * (count - 1)) * 100) / 100;
  return shares;
}

const activeKpisUnder = (record, parentKraItemId) => (record.items || []).filter((i) => i.item_type === "KPI"
  && i.item_status !== "CANCELLED" && String(i.parent_kra_item_id || "") === String(parentKraItemId));

// Re-share the KPI weights under one KRA item. `extra` = KPIs about to be added; returns their shares.
export async function balanceKpis(recordId, parentKraItemId, extra = 0) {
  const record = await recordDetail(recordId);
  const existing = activeKpisUnder(record, parentKraItemId);
  const shares = equalShares(existing.length + extra);
  const changes = existing
    .map((item, i) => ({ item, weight: shares[i] }))
    .filter(({ item, weight }) => Math.abs(Number(item.weight) - weight) > 0.001)
    .sort((a, b) => (a.weight - Number(a.item.weight)) - (b.weight - Number(b.item.weight))); // lower first, so the total never passes 100
  for (const { item, weight } of changes) {
    await api.put(`/items/${item.performance_item_id}`, itemBody(item, { weight }, "KPI weights shared equally under the KRA"));
  }
  return shares.slice(existing.length);
}

async function assign(type, cycleId, employeeId, payload) {
  const summary = await findRecord(cycleId, employeeId);
  const record = await recordDetail(summary.performance_record_id);
  const masterId = type === "KRA" ? payload.kraId : payload.kpiId;
  const master = (await list(type.toLowerCase())).find((m) => m.id === String(masterId));
  if (!master) throw new Error(`Choose a ${type} from the library.`);
  const target = Number(payload.target);
  if (!target || target <= 0) throw new Error("Enter a target greater than 0.");
  let weight = Number(payload.weightage);
  if (type === "KPI") {
    if (!payload.parentKraItemId) throw new Error("Choose the parent KRA first.");
    [weight] = await balanceKpis(record.performance_record_id, payload.parentKraItemId, 1);
  }
  const { data } = await api.post("/assignments", {
    record_ids: [record.performance_record_id],
    assignment: {
      item_type: type,
      master_id: Number(masterId),
      item_title: master.name,
      item_description: master.description || "",
      weight,
      target,
      measurement_unit: type === "KPI" ? master.unit : "%",
      measurement_direction: type === "KPI" ? DIRECTION_TO_API[master.direction] : "HIGHER_IS_BETTER",
      ...(type === "KPI" && payload.parentKraItemId
        ? { parent_kra_item_id: Number(payload.parentKraItemId) }
        : {}),
      due_date: record.measurement_end,
      reason: payload.reason || `${type} assigned`,
    },
    reason: payload.reason || `${type} assigned`,
  });
  return ok(data);
}

// [UPDATED] A corrected item keeps its own measurement scale (unit); items without a scale get 1 to 5.
const unitFor = (item) => (parseScaleUnit(item.measurement_unit) ? item.measurement_unit : scaleUnit(1, 5));
async function updateAssignment(assignmentId, payload) {
  const cached = cachedItem(assignmentId);
  if (!cached) throw new Error("Reload the page and try again.");
  const me = await getMe();
  if (cached.item.change_owner_employee_id !== me.employee_id) {
    throw new Error(`Only ${nameOf(cached.item.change_owner_employee_id)}, who assigned this item, can change it.`);
  }
  // KPI weights are shared automatically, so only the target of a KPI changes here.
  const weight = cached.item.item_type === "KPI" ? cached.item.weight : payload.weightage;
  const { data } = await api.put(`/items/${assignmentId}`, itemBody(cached.item, { weight, target: payload.target, measurement_unit: unitFor(cached.item) }, payload.reason || "Assignment updated"));
  return ok(data);
}

async function removeAssignment(assignmentId, reason = "Assignment removed") {
  const cached = cachedItem(assignmentId);
  const recordId = cached?.record?.performance_record_id;
  const result = await api.delete(`/items/${assignmentId}`, { data: { reason } });
  // The KPIs left under that KRA share its 100% again.
  if (cached?.item.item_type === "KPI" && cached.item.parent_kra_item_id) {
    await balanceKpis(recordId, cached.item.parent_kra_item_id, 0);
  }
  return result;
}

export async function assignToMany(type, cycleId, employeeIds = [], payload = {}) {
  if (!employeeIds.length) throw new Error("Pick at least one employee.");
  const masterId = payload.masterId;
  const master = (await list(type.toLowerCase())).find((m) => m.id === String(masterId));
  if (!master) throw new Error(`Choose a ${type} from the library.`);
  const target = Number(payload.target);
  if (!target || target <= 0) throw new Error("Enter a target greater than 0.");
  const records = await listRecords(cycleId);
  const recordIds = employeeIds.map((id) => records.find((r) => r.employee_id === id)?.performance_record_id).filter(Boolean);
  if (recordIds.length !== employeeIds.length) throw new Error("Some of the chosen people are not part of this cycle any more. Reload the page.");
  if (type === "KPI") {
    // [UPDATED] A KPI goes under each person's own KRA item and the KPI weights there are re-shared, one person at a time.
    if (!master.parentKraId) throw new Error("This KPI has no parent KRA yet. HR links it in the KPI library.");
    for (const recordId of recordIds) {
      const record = await recordDetail(recordId);
      const parent = (record.items || []).find((i) => i.item_type === "KRA" && i.item_status !== "CANCELLED" && String(i.kra_id) === String(master.parentKraId));
      if (!parent) throw new Error(`${record.employee_name || record.employee_id} does not have the KRA of this KPI yet. Assign the KRA first.`);
      await assign("KPI", cycleId, record.employee_id, { kpiId: masterId, parentKraItemId: parent.performance_item_id, target });
    }
    return ok({ record_ids: recordIds });
  }
  const weight = Number(payload.weightage);
  if (!weight || weight <= 0 || weight > 100) throw new Error("Enter a weight between 1 and 100.");
  const cycle = await cycleById(cycleId);
  const { data } = await api.post("/assignments", {
    record_ids: recordIds,
    assignment: {
      item_type: type,
      master_id: Number(masterId),
      item_title: master.name,
      item_description: master.description || "",
      weight,
      target,
      measurement_unit: type === "KPI" ? master.unit : "%",
      measurement_direction: type === "KPI" ? DIRECTION_TO_API[master.direction] : "HIGHER_IS_BETTER",
      due_date: cycle?.endDate,
      reason: payload.reason || `${type} assigned to ${recordIds.length} people`,
    },
    reason: payload.reason || `${type} assigned to ${recordIds.length} people`,
  });
  return ok(data);
}

export const assignKra = (cycleId, employeeId, payload) => assign("KRA", cycleId, employeeId, payload);
export const assignKpi = (cycleId, employeeId, payload) => assign("KPI", cycleId, employeeId, payload);
export const updateKraAssignment = updateAssignment;
export const updateKpiAssignment = updateAssignment;
export const removeKraAssignment = removeAssignment;
export const removeKpiAssignment = removeAssignment;

export async function getAssignableEmployees(cycleId, scope = "org") {
  if (!cycleId) return ok([]);
  const [me, lookups] = await Promise.all([getMe(), getLookupsCached()]);
  const records = await listRecords(cycleId);
  if (scope === "team") {
    // Managers assign only to direct reports (GET /employees/me/direct-reports); never to themselves.
    const { data: reports } = await api.get("/employees/me/direct-reports");
    const byEmployee = new Map(records.map((r) => [r.employee_id, r]));
    return ok(reports
      .filter((e) => e.employee_id !== me.employee_id)
      .map((e) => {
        const r = byEmployee.get(e.employee_id);
        return {
          ...toEmployee(e, lookups, { managerId: me.employee_id }),
          recordId: r?.performance_record_id || null,
          inCycle: Boolean(r && r.eligibility_status === "ELIGIBLE"),
          isDirectReport: true,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)));
  }
  const org = can(me, "performance.org.read", ORG_SCOPES);
  const rows = records.filter((r) => r.employee_id !== me.employee_id && (org || r.review_manager_employee_id === me.employee_id));
  const details = await pMapSettled(rows, (r) => recordDetail(r.performance_record_id), 6, null);
  // [NEW] HR review list: how many goals, KRAs and KPIs each person has, and the goal sheet status.
  const countOf = (d, type) => (d.items || []).filter((i) => i.item_type === type && i.item_status !== "CANCELLED").length;
  return ok(details.filter(Boolean).map((d) => ({
    ...employeeFromRecord(d, lookups), recordId: d.performance_record_id, inCycle: true,
    goalCount: countOf(d, "GOAL"), kraCount: countOf(d, "KRA"), kpiCount: countOf(d, "KPI"), sheetStatus: sheetStatusOf(d),
  }))
    .sort((a, b) => a.name.localeCompare(b.name)));
}

export async function getAssignmentPermissions() {
  const me = await getMe();
  const scope = libraryScopeOf(me);
  return ok({
    canAssign: can(me, "kra_kpi.assign"),
    canAssignOrg: can(me, "kra_kpi.assign", ORG_SCOPES),
    canReadLibrary: Boolean(scope),
    libraryScope: scope,
    meId: me.employee_id,
  });
}

export async function getKpiMeasurements(itemId) {
  const { data } = await api.get(`/items/${itemId}/measurements`);
  return ok(data.map((m) => ({
    id: m.measurement_id,
    periodStart: m.period_start,
    periodEnd: m.period_end,
    value: Number(m.actual_value),
    revision: m.revision_no,
    by: nameOf(m.recorded_by_employee_id),
    at: isoUtc(m.recorded_at || m.created_at),
  })));
}

export const addKpiMeasurement = (itemId, { periodStart, periodEnd, value, reason }) =>
  api.post(`/items/${itemId}/measurements`, {
    period_start: periodStart,
    period_end: periodEnd || periodStart || utcToday(),
    actual_value: Number(value),
    reason: reasonOf(reason, "KPI measurement recorded"),
  });